/**
 * CaprionJourneyStatus — "View Journey Status" reporting screen.
 *
 * @origin: v3 | @since: native | @used-by: LND:CAPRION
 *
 * One row per (user, application_id) that has at least one Caprion
 * `check_status` call: username, application_id, derived final_status, the BRE1
 * and BRE2 DECISION responses (calls whose status_type is not CREATED), the
 * journey page the user is currently on, and the source-slug they arrived
 * through. Clicking a BRE cell opens a JSON-viewer dialog over the verbatim
 * response body.
 *
 * A borrower with several applications (a re-apply after a rejection, or
 * parallel test runs) therefore gets one row PER application, not one merged row.
 *
 * Backend:
 *   GET /api/admin/caprion/journey_status/      -> paginated rows + columns
 *   GET /api/admin/caprion/journey_status/csv/  -> CSV of the FILTERED set
 *   (backend/los_app/admin_views.py, routed in backend/los_app/urls.py)
 *
 * Both are SUPERUSER ONLY and strictly read-only — they read the persisted
 * CaprionRequestResponse audit trail and never call Caprion's live API, so
 * opening/refreshing this screen bills nothing. That is the key difference from
 * the Caprion "Check Status" Fixer tool, which DOES hit the lender.
 *
 * COLUMN LIST IS DATA-DRIVEN: the response's `columns` block drives both the
 * table headers and the CSV field-picker, because the number of BRE columns
 * depends on how many decisions the most-progressed application received.
 *
 * PII POSTURE: rows carry phone-number usernames, names and full lender
 * decision payloads. Nothing is written to console/localStorage/sessionStorage.
 */
import React from "react";
import {
    AlertTriangle,
    ArrowLeft,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Download,
    Loader2,
    RefreshCw,
    Route as RouteIcon,
    Search,
    ShieldAlert,
    X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    adminService,
    getAdminApiErrorMessage,
    getAdminApiErrorStatus,
    isAdminApiCanceled,
    isAdminApiNetworkError,
    type CaprionCheckStatusCall,
    type CaprionJourneyColumn,
    type CaprionJourneyRow,
    type CaprionJourneyStatusResponse,
} from "./adminApi";
import lmsStaffService, {
    type ApprovedPipelineUser,
} from "./lms_staff/lmsStaffApi";

/** Sentinel for "no filter" — Radix Select forbids an empty-string value. */
const ALL = "__all__";
const EM_DASH = "—";
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;

/** Pinned to the left of the table; everything else keeps backend order. */
const PRIMARY_KEYS = ["name", "phone_number", "application_id", "final_status"];

/**
 * Pinned to the far right, after the BRE columns. The backend already
 * emits it last, but PRIMARY_KEYS reorders the head of the list, so the tail
 * has to be pinned explicitly or a future primary/hidden change could drift it.
 */
const TRAILING_KEYS = ["last_called_at"];

/** Scalar columns hidden from the TABLE only — still available in the CSV. */
const TABLE_HIDDEN_KEYS = new Set([
    "applicant_id",
    "current_page_slug",
    "first_called_at",
    "final_lender_status",
]);

/**
 * Narrow an arbitrary response body to the payload this screen renders.
 *
 * WHY: the component used to read `data.choices.final_status` after only
 * optional-chaining `data`, so ANY body that was not this exact object (an
 * SPA `index.html` fallback served for /api/..., a proxy error page, an older
 * backend revision without `choices`) crashed the whole screen with
 * "Cannot read properties of undefined (reading 'final_status')". A report
 * screen must degrade to a visible error, never to a blank page — so the
 * payload is validated once here instead of being trusted at ~6 read sites.
 */
function isJourneyStatusPayload(value: unknown): value is CaprionJourneyStatusResponse {
    if (typeof value !== "object" || value === null) return false;
    const d = value as Partial<CaprionJourneyStatusResponse>;
    return Array.isArray(d.results) && Array.isArray(d.columns);
}

/** Validate an index-signature cell as a check_status call object. */
function asCall(value: unknown): CaprionCheckStatusCall | null {
    if (typeof value !== "object" || value === null) return null;
    const c = value as Partial<CaprionCheckStatusCall>;
    return typeof c.id === "number" ? (value as CaprionCheckStatusCall) : null;
}

/** Colour-code the terminal status so the table scans at a glance. */
function statusClass(status: string | null): string {
    switch (status) {
        case "APPROVED":
            return "bg-green-100 text-green-800 hover:bg-green-100";
        case "REJECTED":
            return "bg-red-100 text-red-800 hover:bg-red-100";
        case "OPS_VERIFICATION":
            return "bg-blue-100 text-blue-800 hover:bg-blue-100";
        case "WITHDRAWN":
            return "bg-gray-200 text-gray-700 hover:bg-gray-200";
        case "PENDING":
            return "bg-amber-100 text-amber-800 hover:bg-amber-100";
        default:
            return "bg-slate-100 text-slate-700 hover:bg-slate-100";
    }
}

function formatTimestamp(value: unknown): string {
    if (typeof value !== "string" || !value) return EM_DASH;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function renderScalar(value: unknown): string {
    if (value === null || value === undefined || value === "") return EM_DASH;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
}

interface CaprionJourneyStatusProps {
    onBack?: () => void;
}

const CaprionJourneyStatus: React.FC<CaprionJourneyStatusProps> = ({ onBack }) => {
    const [data, setData] = React.useState<CaprionJourneyStatusResponse | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [errorStatus, setErrorStatus] = React.useState<number | undefined>();
    const [isNetworkError, setIsNetworkError] = React.useState(false);
    const [pipelineExpanded, setPipelineExpanded] = React.useState(true);

    const pipelineQuery = useQuery({
        queryKey: ["caprion-approved-pipeline"],
        queryFn: async () => {
            const response = await lmsStaffService.getApprovedPipeline();
            return response.data;
        },
        retry: false,
    });

    // ── Per-row Caprion CheckStatus ───────────────────────────────────────────
    // Keyed by user_id (NOT application_id) because the table's rows are keyed
    // that way and two users could in principle be mapped to the same stale
    // application_id — keying on user_id guarantees the result lands in the row
    // the operator actually clicked.
    //
    // Fired on demand only: CheckStatus hits Caprion's LIVE API, so it must
    // never run automatically on render or refetch.
    const [pipelineStatusBusy, setPipelineStatusBusy] = React.useState<number | null>(null);
    const [pipelineStatusResult, setPipelineStatusResult] = React.useState<
        Record<number, { status?: string; error?: string }>
    >({});

    const handlePipelineCheckStatus = async (u: ApprovedPipelineUser) => {
        const appId = (u.application_id || "").trim();
        if (!appId) return;
        setPipelineStatusBusy(u.user_id);
        // Drop any previous result for this row so a stale status never sits
        // next to an in-flight request.
        setPipelineStatusResult((prev) => {
            const next = { ...prev };
            delete next[u.user_id];
            return next;
        });
        try {
            const res = await adminService.caprionCheckStatus({ application_id: appId });
            // Caprion's CheckStatus nests the value as data.status.status — the
            // outer `status` is the block, the inner one is the human-readable
            // state ("Approved" / "Pending" / …). Fall back through the shallower
            // shapes so a payload variant renders something rather than nothing.
            const payload = res.data ?? {};
            const resolved =
                payload?.data?.status?.status
                ?? payload?.status?.status
                ?? payload?.data?.status
                ?? null;
            setPipelineStatusResult((prev) => ({
                ...prev,
                [u.user_id]: typeof resolved === "string" && resolved
                    ? { status: resolved }
                    : { error: payload?.error ? String(payload.error) : "No status in response" },
            }));
        } catch (err) {
            setPipelineStatusResult((prev) => ({
                ...prev,
                [u.user_id]: { error: getAdminApiErrorMessage(err, "CheckStatus call failed.") },
            }));
        } finally {
            setPipelineStatusBusy(null);
        }
    };

    /** Colour the returned status the same way the rest of this page reads. */
    const pipelineStatusClass = (status: string) => {
        const s = status.toLowerCase();
        if (s.includes("approv")) return "bg-green-100 text-green-800";
        if (s.includes("reject") || s.includes("withdraw") || s.includes("cancel")) return "bg-red-100 text-red-800";
        if (s.includes("disburs")) return "bg-blue-100 text-blue-800";
        return "bg-amber-100 text-amber-800";
    };

    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    const [finalStatus, setFinalStatus] = React.useState(ALL);
    const [sourceSlug, setSourceSlug] = React.useState(ALL);
    const [pageName, setPageName] = React.useState(ALL);
    const [createdFrom, setCreatedFrom] = React.useState("");
    const [createdTo, setCreatedTo] = React.useState("");

    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(PAGE_SIZE_OPTIONS[0]);
    const [refreshTick, setRefreshTick] = React.useState(0);

    const [viewerCall, setViewerCall] = React.useState<CaprionCheckStatusCall | null>(null);
    const [viewerTitle, setViewerTitle] = React.useState("");
    const [viewerRow, setViewerRow] = React.useState<CaprionJourneyRow | null>(null);

    // ── Lazily-fetched response body for the JSON viewer ─────────────────────
    // The list endpoint no longer ships `response` on each BRE cell (those
    // bodies average ~2.7 KB and shipping one per call per row is what made this
    // screen slow). The table never needed them — a cell shows a status badge
    // and a "JSON" button — so the body is fetched here, for the single call the
    // operator just clicked.
    const [viewerBody, setViewerBody] = React.useState<unknown>(null);
    const [viewerLoading, setViewerLoading] = React.useState(false);
    const [viewerError, setViewerError] = React.useState<string | null>(null);

    /** Open the viewer for one BRE cell and fetch its stored response body. */
    const openJsonViewer = async (
        row: CaprionJourneyRow,
        call: CaprionCheckStatusCall,
        label: string,
    ) => {
        setViewerRow(row);
        setViewerCall(call);
        setViewerTitle(label);
        // A payload that already carries the body (e.g. an older cached
        // response) is used as-is rather than re-fetched.
        if (call.response !== undefined && call.response !== null) {
            setViewerBody(call.response);
            setViewerError(null);
            return;
        }
        setViewerBody(null);
        setViewerError(null);
        setViewerLoading(true);
        try {
            const res = await adminService.caprionCheckStatusResponse(call.id);
            setViewerBody(res.data.response ?? null);
        } catch (err) {
            setViewerError(getAdminApiErrorMessage(err, "Could not load the response body."));
        } finally {
            setViewerLoading(false);
        }
    };

    // `null` = not initialised; on first load every field is checked, per the
    // requirement that the CSV picker defaults to all-selected.
    const [selectedFields, setSelectedFields] = React.useState<Set<string> | null>(null);
    const [downloading, setDownloading] = React.useState(false);

    React.useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    const filterParams = React.useMemo(
        () => ({
            ...(search ? { search } : {}),
            ...(finalStatus !== ALL ? { final_status: finalStatus } : {}),
            ...(sourceSlug !== ALL ? { source_slug: sourceSlug } : {}),
            ...(pageName !== ALL ? { page_name: pageName } : {}),
            ...(createdFrom ? { created_from: createdFrom } : {}),
            ...(createdTo ? { created_to: createdTo } : {}),
        }),
        [search, finalStatus, sourceSlug, pageName, createdFrom, createdTo],
    );

    React.useEffect(() => {
        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            setErrorStatus(undefined);
            setIsNetworkError(false);
            try {
                const response = await adminService.caprionJourneyStatus(
                    { page, page_size: pageSize, ...filterParams },
                    { signal: controller.signal },
                );
                if (!active) return;
                if (!isJourneyStatusPayload(response.data)) {
                    setData(null);
                    setError(
                        "The server returned an unexpected response for Caprion journey status.",
                    );
                    return;
                }
                setData(response.data);
                setSelectedFields((current) =>
                    current ?? new Set(response.data.columns.map((c) => c.key)),
                );
            } catch (err) {
                if (isAdminApiCanceled(err) || !active) return;
                setErrorStatus(getAdminApiErrorStatus(err));
                setIsNetworkError(isAdminApiNetworkError(err));
                setError(getAdminApiErrorMessage(err, "Failed to load Caprion journey status."));
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => {
            active = false;
            controller.abort();
        };
    }, [page, pageSize, filterParams, refreshTick]);

    const columns = data?.columns ?? [];
    const rows = data?.results ?? [];

    const tableColumns = React.useMemo(() => {
        const visible = columns.filter((c) => !TABLE_HIDDEN_KEYS.has(c.key));
        const pick = (keys: string[]) =>
            keys
                .map((k) => visible.find((c) => c.key === k))
                .filter((c): c is CaprionJourneyColumn => Boolean(c));
        const middle = visible.filter(
            (c) => !PRIMARY_KEYS.includes(c.key) && !TRAILING_KEYS.includes(c.key),
        );
        return [...pick(PRIMARY_KEYS), ...middle, ...pick(TRAILING_KEYS)];
    }, [columns]);

    const activeFilterCount = Object.keys(filterParams).length;

    const resetFilters = () => {
        setSearchInput("");
        setSearch("");
        setFinalStatus(ALL);
        setSourceSlug(ALL);
        setPageName(ALL);
        setCreatedFrom("");
        setCreatedTo("");
        setPage(1);
    };

    const toggleField = (key: string) =>
        setSelectedFields((current) => {
            const next = new Set(current ?? columns.map((c) => c.key));
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const selectedCount = selectedFields
        ? columns.filter((c) => selectedFields.has(c.key)).length
        : columns.length;

    const handleDownloadCsv = async () => {
        // Send keys in BACKEND column order (not click order) so the CSV's
        // column order always matches the table on screen.
        const chosen = columns
            .map((c) => c.key)
            .filter((key) => (selectedFields ? selectedFields.has(key) : true));
        if (chosen.length === 0) return;

        setDownloading(true);
        try {
            const response = await adminService.caprionJourneyStatusCsv({
                ...filterParams,
                fields: chosen.join(","),
            });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `caprion_bre_analysis_${new Date()
                .toISOString()
                .slice(0, 19)
                .replace(/[:T]/g, "")}.csv`;
            document.body.appendChild(link);
            link.click();
            // Revoking synchronously can cancel the download in some browsers.
            window.setTimeout(() => {
                window.URL.revokeObjectURL(url);
                link.remove();
            }, 0);
        } catch (err) {
            setError(getAdminApiErrorMessage(err, "Failed to download CSV."));
        } finally {
            setDownloading(false);
        }
    };

    // A 403 must NOT read as "no data" — that is the opposite of the truth.
    if (errorStatus === 403) {
        return (
            <div className="p-6">
                <div className="mx-auto max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
                    <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-amber-600" />
                    <h3 className="mb-1 text-lg font-semibold text-amber-900">
                        Superuser access required
                    </h3>
                    <p className="text-sm text-amber-800">
                        This screen shows borrower PII and full lender decision payloads, so it
                        is restricted to superusers.
                    </p>
                    {onBack && (
                        <Button variant="outline" className="mt-4" onClick={onBack}>
                            <ArrowLeft className="mr-2 h-4 w-4" /> Go back
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 p-4">
            {/* ── Approved — Pending Disbursal ──────────────────────────────────
                Users approved by Caprion but not yet disbursed. Always shown
                (even when count == 0) so ops staff see the section exists. */}
            {!pipelineQuery.isLoading && (
                <Card className="border-t-4 border-amber-400">
                    <CardHeader
                        className="bg-amber-50 cursor-pointer select-none py-3 px-4"
                        onClick={() => setPipelineExpanded((v) => !v)}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CardTitle className="text-base font-semibold text-amber-700">
                                    ⏳ Approved — Pending Disbursal
                                </CardTitle>
                                <Badge className="bg-amber-100 text-amber-800 text-xs font-bold">
                                    {pipelineQuery.data?.count ?? 0}
                                </Badge>
                                {(pipelineQuery.data?.total_approved_amount) && (
                                    <span className="text-sm font-semibold text-amber-700">
                                        ₹{Number(pipelineQuery.data.total_approved_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                    </span>
                                )}
                                <span className="text-xs text-gray-500">sanctioned, awaiting disbursal</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {pipelineExpanded
                                    ? <ChevronUp className="h-4 w-4 text-gray-500" />
                                    : <ChevronDown className="h-4 w-4 text-gray-500" />}
                            </div>
                        </div>
                    </CardHeader>

                    {pipelineExpanded && (
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-amber-50/50">
                                            <TableHead className="text-xs whitespace-nowrap">Name</TableHead>
                                            <TableHead className="text-xs whitespace-nowrap">Phone</TableHead>
                                            <TableHead className="text-xs whitespace-nowrap">Approved Amt</TableHead>
                                            <TableHead className="text-xs whitespace-nowrap">Loan Amt</TableHead>
                                            <TableHead className="text-xs whitespace-nowrap">Current Stage</TableHead>
                                            {/* Renamed from "Last Updated": for an approved-pending-disbursal
                                                row this timestamp IS the completion of the application (the
                                                most-decisive CheckStatus call), not an arbitrary edit time. */}
                                            <TableHead className="text-xs whitespace-nowrap">Application Completed</TableHead>
                                            <TableHead className="text-xs whitespace-nowrap">Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(pipelineQuery.data?.results ?? []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-4 text-gray-400 italic">
                                                    No pending disbursals
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            (pipelineQuery.data?.results ?? []).map((u: ApprovedPipelineUser, idx: number) => (
                                                <TableRow key={u.user_id ?? idx} className="hover:bg-amber-50/30">
                                                    <TableCell className="font-medium text-sm whitespace-nowrap">
                                                        {u.name || "—"}
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs whitespace-nowrap">
                                                        {u.phone || "—"}
                                                    </TableCell>
                                                    <TableCell className="text-sm font-semibold text-amber-700 whitespace-nowrap">
                                                        {u.approved_loan_amount != null
                                                            ? `₹${Number(u.approved_loan_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                                                            : u.loan_amount != null
                                                                ? `₹${Number(u.loan_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                                                                : "—"}
                                                    </TableCell>
                                                    <TableCell className="text-xs whitespace-nowrap">
                                                        {u.loan_amount != null
                                                            ? `₹${Number(u.loan_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                                                            : "—"}
                                                    </TableCell>
                                                    <TableCell className="text-xs whitespace-nowrap text-gray-500">
                                                        {u.stage_name || "—"}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                                                        {u.last_updated ? u.last_updated.slice(0, 10) : "—"}
                                                    </TableCell>
                                                    {/* On-demand live CheckStatus. The button is disabled when the
                                                        row has no application_id, so we never call the lender with
                                                        an empty id. The result replaces the button in place. */}
                                                    <TableCell className="text-xs whitespace-nowrap">
                                                        {pipelineStatusResult[u.user_id]?.status ? (
                                                            <div className="flex items-center gap-2">
                                                                <Badge className={`text-xs font-semibold ${pipelineStatusClass(pipelineStatusResult[u.user_id].status!)}`}>
                                                                    {pipelineStatusResult[u.user_id].status}
                                                                </Badge>
                                                                <button
                                                                    type="button"
                                                                    title="Re-check"
                                                                    className="text-gray-400 hover:text-gray-700"
                                                                    onClick={() => handlePipelineCheckStatus(u)}
                                                                    disabled={pipelineStatusBusy !== null}
                                                                >
                                                                    <RefreshCw className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        ) : pipelineStatusResult[u.user_id]?.error ? (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-red-600" title={pipelineStatusResult[u.user_id].error}>
                                                                    Failed
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    title="Retry"
                                                                    className="text-gray-400 hover:text-gray-700"
                                                                    onClick={() => handlePipelineCheckStatus(u)}
                                                                    disabled={pipelineStatusBusy !== null}
                                                                >
                                                                    <RefreshCw className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-7 text-xs"
                                                                onClick={() => handlePipelineCheckStatus(u)}
                                                                disabled={pipelineStatusBusy !== null || !u.application_id}
                                                                title={u.application_id
                                                                    ? `Call Caprion CheckStatus for application ${u.application_id}`
                                                                    : "No Caprion application ID on record for this user"}
                                                            >
                                                                {pipelineStatusBusy === u.user_id
                                                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                                                    : "Check Status"}
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    )}
                </Card>
            )}

            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    {onBack && (
                        <Button variant="ghost" size="icon" onClick={onBack} title="Back">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                    )}
                    <div>
                        <h2 className="flex items-center gap-2 text-lg font-semibold">
                            <RouteIcon className="h-5 w-5 text-intellend-green" />
                            BRE Analysis (Caprion)
                        </h2>
                        <p className="text-xs text-gray-500">
                            One row per application · BRE1 &amp; BRE2 decision responses · current
                            journey page · source slug. Read-only — never calls Caprion&apos;s API.
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRefreshTick((t) => t + 1)}
                        disabled={loading}
                    >
                        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>

                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" disabled={columns.length === 0}>
                                <Download className="mr-2 h-4 w-4" />
                                Download CSV
                                <Badge variant="secondary" className="ml-2">
                                    {selectedCount}/{columns.length}
                                </Badge>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80" align="end">
                            <div className="mb-3 flex items-center justify-between">
                                <p className="text-sm font-semibold">Fields to download</p>
                                <div className="flex gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelectedFields(new Set(columns.map((c) => c.key)))}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelectedFields(new Set())}
                                    >
                                        None
                                    </Button>
                                </div>
                            </div>
                            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                                {columns.map((column) => (
                                    <div key={column.key} className="flex items-center gap-2">
                                        <Checkbox
                                            id={`field-${column.key}`}
                                            checked={selectedFields ? selectedFields.has(column.key) : true}
                                            onCheckedChange={() => toggleField(column.key)}
                                        />
                                        <Label
                                            htmlFor={`field-${column.key}`}
                                            className="cursor-pointer text-xs font-normal"
                                        >
                                            {column.label}
                                            {column.is_json && (
                                                <span className="ml-1 text-[10px] text-gray-400">(JSON)</span>
                                            )}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 text-[11px] text-gray-500">
                                Exports every row matching the current filters, not just this page.
                            </p>
                            <Button
                                className="mt-2 w-full"
                                size="sm"
                                onClick={handleDownloadCsv}
                                disabled={downloading || selectedCount === 0}
                            >
                                {downloading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="mr-2 h-4 w-4" />
                                )}
                                Download {selectedCount} field{selectedCount === 1 ? "" : "s"}
                            </Button>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-lg border bg-white p-3">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[220px] flex-1">
                        <Label className="mb-1 block text-xs text-gray-500">Search</Label>
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <Input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Phone, name, application ID, order ID, loan code, page…"
                                className="pl-8"
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    onClick={() => setSearchInput("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label="Clear search"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="w-[165px]">
                        <Label className="mb-1 block text-xs text-gray-500">Final status</Label>
                        <Select
                            value={finalStatus}
                            onValueChange={(v) => {
                                setFinalStatus(v);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All statuses</SelectItem>
                                {(data?.choices?.final_status ?? []).map((v) => (
                                    <SelectItem key={v} value={v}>
                                        {v}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-[165px]">
                        <Label className="mb-1 block text-xs text-gray-500">Source slug</Label>
                        <Select
                            value={sourceSlug}
                            onValueChange={(v) => {
                                setSourceSlug(v);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All sources</SelectItem>
                                {(data?.choices?.source_slug ?? []).map((v) => (
                                    <SelectItem key={v} value={v}>
                                        {v}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-[200px]">
                        <Label className="mb-1 block text-xs text-gray-500">Current page</Label>
                        <Select
                            value={pageName}
                            onValueChange={(v) => {
                                setPageName(v);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All pages</SelectItem>
                                {(data?.choices?.page_name ?? []).map((v) => (
                                    <SelectItem key={v} value={v}>
                                        {v}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-[150px]">
                        <Label className="mb-1 block text-xs text-gray-500">From</Label>
                        <Input
                            type="date"
                            value={createdFrom}
                            onChange={(e) => {
                                setCreatedFrom(e.target.value);
                                setPage(1);
                            }}
                        />
                    </div>

                    <div className="w-[150px]">
                        <Label className="mb-1 block text-xs text-gray-500">To</Label>
                        <Input
                            type="date"
                            value={createdTo}
                            onChange={(e) => {
                                setCreatedTo(e.target.value);
                                setPage(1);
                            }}
                        />
                    </div>

                    {activeFilterCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={resetFilters}>
                            <X className="mr-1 h-4 w-4" />
                            Clear ({activeFilterCount})
                        </Button>
                    )}
                </div>
            </div>

            {/* Error (non-403) */}
            {error && errorStatus !== 403 && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                    <div className="text-sm text-red-800">
                        <p className="font-medium">
                            {isNetworkError ? "Cannot reach the server" : "Request failed"}
                        </p>
                        <p className="text-xs">{error}</p>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="rounded-lg border bg-white">
                <div className="flex items-center justify-between border-b px-3 py-2">
                    <p className="text-xs text-gray-500">
                        {loading
                            ? "Loading…"
                            : `${data?.total ?? 0} user${(data?.total ?? 0) === 1 ? "" : "s"} · showing ${rows.length}`}
                    </p>
                    <div className="flex items-center gap-2">
                        <Label className="text-xs text-gray-500">Rows</Label>
                        <Select
                            value={String(pageSize)}
                            onValueChange={(v) => {
                                setPageSize(Number(v));
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="h-7 w-[70px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <SelectItem key={size} value={String(size)}>
                                        {size}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {tableColumns.map((column) => (
                                    <TableHead key={column.key} className="whitespace-nowrap text-xs">
                                        {column.label}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={Math.max(1, tableColumns.length)}
                                        className="py-10 text-center"
                                    >
                                        <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && rows.length === 0 && !error && (
                                <TableRow>
                                    <TableCell
                                        colSpan={Math.max(1, tableColumns.length)}
                                        className="py-10 text-center text-sm text-gray-500"
                                    >
                                        {activeFilterCount > 0
                                            ? "No applications match these filters."
                                            : "No Caprion BRE responses recorded yet."}
                                    </TableCell>
                                </TableRow>
                            )}

                            {rows.map((row) => (
                                <TableRow key={row.user_id} className="text-xs">
                                    {tableColumns.map((column) => {
                                        const value = row[column.key];

                                        // bre_N — a button that opens the JSON viewer.
                                        if (column.is_json) {
                                            const call = asCall(value);
                                            if (!call) {
                                                return (
                                                    <TableCell key={column.key} className="text-gray-300">
                                                        {EM_DASH}
                                                    </TableCell>
                                                );
                                            }
                                            return (
                                                <TableCell key={column.key} className="whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={() => openJsonViewer(row, call, column.label)}
                                                        className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 hover:border-intellend-green hover:bg-green-50"
                                                        title="View full JSON response"
                                                    >
                                                        <Badge
                                                            variant="secondary"
                                                            className={`${statusClass(call.status_type)} px-1 py-0 text-[10px]`}
                                                        >
                                                            {call.status_type || "?"}
                                                        </Badge>
                                                        <span className="text-[10px] text-gray-500">JSON</span>
                                                    </button>
                                                </TableCell>
                                            );
                                        }

                                        if (column.key === "final_status") {
                                            return (
                                                <TableCell key={column.key} className="whitespace-nowrap">
                                                    <Badge
                                                        variant="secondary"
                                                        className={statusClass(row.final_status)}
                                                    >
                                                        {row.final_status || EM_DASH}
                                                    </Badge>
                                                </TableCell>
                                            );
                                        }

                                        if (column.key === "last_called_at") {
                                            return (
                                                <TableCell key={column.key} className="whitespace-nowrap">
                                                    {formatTimestamp(value)}
                                                </TableCell>
                                            );
                                        }

                                        if (column.key === "current_page_number") {
                                            return (
                                                <TableCell key={column.key} className="whitespace-nowrap">
                                                    {row.current_page_number ?? EM_DASH}
                                                </TableCell>
                                            );
                                        }

                                        if (column.key === "name") {
                                            return (
                                                <TableCell
                                                    key={column.key}
                                                    className="whitespace-nowrap font-medium"
                                                >
                                                    {renderScalar(value)}
                                                    <span className="ml-1 text-[10px] text-gray-400">
                                                        #{row.user_id}
                                                    </span>
                                                </TableCell>
                                            );
                                        }

                                        if (column.key === "approved_loan_amount") {
                                            const amt = value != null && value !== "" ? Number(value) : null;
                                            return (
                                                <TableCell key={column.key} className="whitespace-nowrap text-xs font-medium text-gray-700">
                                                    {amt != null && !Number.isNaN(amt)
                                                        ? `₹${amt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                                                        : EM_DASH}
                                                </TableCell>
                                            );
                                        }

                                        if (
                                            column.key === "loan_amount" ||
                                            column.key === "offer_loan_amt_net" ||
                                            column.key === "offer_daily_repayment" ||
                                            column.key === "offer_total_fee" ||
                                            column.key === "offer_repay_total"
                                        ) {
                                            const amt = value != null && value !== "" ? Number(value) : null;
                                            return (
                                                <TableCell key={column.key} className="whitespace-nowrap text-xs text-gray-600">
                                                    {amt != null && !Number.isNaN(amt)
                                                        ? `₹${amt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                                                        : EM_DASH}
                                                </TableCell>
                                            );
                                        }

                                        return (
                                            <TableCell key={column.key} className="max-w-[220px] truncate">
                                                {renderScalar(value)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                {(data?.total_pages ?? 0) > 1 && (
                    <div className="flex items-center justify-between border-t px-3 py-2">
                        <p className="text-xs text-gray-500">
                            Page {data?.page} of {data?.total_pages}
                        </p>
                        <div className="flex gap-1">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={loading || page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={loading || page >= (data?.total_pages ?? 1)}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* JSON viewer popup */}
            <Dialog
                open={Boolean(viewerCall)}
                onOpenChange={(open) => {
                    if (!open) {
                        setViewerCall(null);
                        setViewerRow(null);
                        // Clear the lazily-fetched body too, so reopening a
                        // different cell can never flash the previous call's
                        // JSON before its own fetch lands.
                        setViewerBody(null);
                        setViewerError(null);
                        setViewerLoading(false);
                    }
                }}
            >
                {/*
                  * WIDTH CONTAINMENT: DialogContent is a CSS grid, and a <pre>
                  * reports an intrinsic min-width equal to its longest line. A
                  * deep Caprion payload therefore blew the grid track wider than
                  * max-w-3xl, overflowing the panel and pushing the Copy JSON
                  * button outside it. `min-w-0` on every level lets the track
                  * shrink below that intrinsic width, and the <pre> wraps
                  * instead of forcing a horizontal scroll of the whole dialog.
                  */}
                <DialogContent className="grid max-h-[85vh] w-[min(56rem,95vw)] max-w-none grid-rows-[auto_minmax(0,1fr)] overflow-hidden">
                    <DialogHeader className="min-w-0">
                        <DialogTitle className="pr-8 text-base">
                            <span className="break-words">{viewerTitle}</span>
                            {viewerRow && (
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                    {viewerRow.phone_number ?? viewerRow.name ?? `#${viewerRow.user_id}`}
                                    {" · app "}
                                    {viewerRow.application_id ?? EM_DASH}
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    {viewerCall && (
                        <div className="grid min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)_auto] gap-3">
                            {/* Call metadata, so the payload has context */}
                            <div className="flex flex-wrap gap-2 text-xs">
                                <Badge variant="secondary" className={statusClass(viewerCall.status_type)}>
                                    {viewerCall.status_type || "UNKNOWN"}
                                </Badge>
                                {viewerCall.lender_status && (
                                    <Badge variant="outline">Lender: {viewerCall.lender_status}</Badge>
                                )}
                                {viewerCall.order_id && (
                                    <Badge variant="outline">Order: {viewerCall.order_id}</Badge>
                                )}
                                <Badge variant="outline">HTTP {viewerCall.status_code ?? EM_DASH}</Badge>
                                <Badge variant="outline">
                                    {viewerCall.success ? "Success" : "Failed"}
                                </Badge>
                                {viewerCall.is_final_response && (
                                    <Badge variant="outline">Final response</Badge>
                                )}
                                <Badge variant="outline">{formatTimestamp(viewerCall.created_at)}</Badge>
                            </div>

                            {/*
                              * The verbatim response body, pretty-printed.
                              * whitespace-pre-wrap keeps the JSON indentation
                              * while allowing long values (base64 blobs, URLs)
                              * to wrap; break-words stops a single unbroken
                              * token from re-introducing the overflow.
                              */}
                            {/* Body is fetched on open (see openJsonViewer), so
                                the dialog shows a spinner for the one call being
                                viewed instead of the list paying for every
                                call's body up front. */}
                            <pre className="min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
                                {viewerLoading
                                    ? "Loading response…"
                                    : viewerError
                                        ? `Error: ${viewerError}`
                                        : JSON.stringify(viewerBody ?? null, null, 2)}
                            </pre>

                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        navigator.clipboard?.writeText(
                                            JSON.stringify(viewerBody ?? null, null, 2),
                                        )
                                    }
                                >
                                    Copy JSON
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CaprionJourneyStatus;